import { useEffect, useState } from 'react'
import { LaunchDarklyService } from '@ampcore/client-side-services'
import { FeatureFlag } from '../../types/FeatureFlag.type'

declare global {
  interface Window {
    LD_CID?: string
  }
}

/**
 * useFeatureFlag hook
 * @param flagKey - The LaunchDarkly flag key
 * @param defaultValue - The default value if flag is not available
 */
export function useFeatureFlag(flagKey: FeatureFlag, defaultValue: boolean) {
  const ldService = LaunchDarklyService.getInstance()
  const [flagValue, setFlagValue] = useState<boolean>(defaultValue)

  useEffect(() => {
    const ldClient = ldService.getClient()
    let isMounted = true
    let updateFlag: (() => void) | undefined
    ldService.waitForInitialization().then(() => {
      if (!isMounted) return
      if (!ldClient) return
      updateFlag = () => {
        const value = ldClient.variation(flagKey, defaultValue)
        setFlagValue(value)
      }
      updateFlag() // Set initial value
      ldClient.on(`change:${flagKey}`, updateFlag)
    })
    return () => {
      isMounted = false
      if (ldClient && typeof ldClient.off === 'function' && updateFlag) {
        ldClient.off(`change:${flagKey}`, updateFlag)
      }
    }
  }, [ldService, flagKey, defaultValue])

  return flagValue
}
